import { provideZoneChangeDetection, provideAppInitializer, inject, ErrorHandler, isDevMode, LOCALE_ID, type ApplicationConfig } from "@angular/core";
import {
    AppSettingsService,
    AnalyticsService,
    SentryErrorHandler,
    authInterceptor,
    httpHandlerInterceptor,
    KendoMessageService,
} from "@shared/index";
import { SentryService } from "./shared/analytics-service/sentry.service";
import { IframeMessagesService } from "./shared/iframe-messages-service/iframe-messages.service";
import { AppStartupTimingService } from "@app/shared/services";
import { provideHttpClient, withInterceptors } from "@angular/common/http";
import { MessageService } from "@progress/kendo-angular-l10n";
import { DatePipe, registerLocaleData } from "@angular/common";
import localeFrCA from "@angular/common/locales/fr-CA";
import { provideAnimations } from "@angular/platform-browser/animations";
import { load } from "@progress/kendo-angular-intl";
import calendar from "cldr-data/main/fr/ca-gregorian.json";
import dateFields from "cldr-data/main/fr/dateFields.json";
import numbers from "cldr-data/main/fr/numbers.json";
import timeZoneNames from "cldr-data/main/fr/timeZoneNames.json";
import likelySubtags from "cldr-data/supplemental/likelySubtags.json";
import weekData from "cldr-data/supplemental/weekData.json";
import { appRoutes } from "./app.routing";
import { provideTranslateService } from "@ngx-translate/core";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import { provideRouter } from "@angular/router";
import { concurrencyInterceptor } from "./shared/request-concurrency";

// Global registrations, needed before the first component renders.
registerLocaleData(localeFrCA, "fr-CA");
load(likelySubtags, weekData, numbers, calendar, dateFields, timeZoneNames);

function provideErrorHandler() {
    if (isDevMode()) {
        return new ErrorHandler();
    } else {
        return new SentryErrorHandler();
    }
}

async function appInit(): Promise<void> {
    const config = inject(AppSettingsService);
    // Instantiated eagerly for their constructor side effects.
    inject(SentryService);
    inject(AnalyticsService);
    const iframe = inject(IframeMessagesService);
    const errorHandler = inject(ErrorHandler);
    const appStartupTimingService = inject(AppStartupTimingService);

    console.time("Application responsive elapsed time");
    appStartupTimingService.setStartTime(new Date());

    return config.getAppSettings().then(() => {
        iframe.init();
        if (errorHandler instanceof SentryErrorHandler) {
            errorHandler.initSentry();
        }
    });
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideRouter(appRoutes),
        provideTranslateService({
            loader: provideTranslateHttpLoader({ prefix: "assets/i18n/", suffix: ".json" }),
        }),
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideAppInitializer(appInit),
        provideHttpClient(withInterceptors([authInterceptor, httpHandlerInterceptor, concurrencyInterceptor])),
        { provide: ErrorHandler, useFactory: provideErrorHandler },
        { provide: LOCALE_ID, useValue: localStorage.getItem("lang") ? localStorage.getItem("lang") : "en-CA" },
        { provide: MessageService, useClass: KendoMessageService },
        DatePipe,
    ],
};
